import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Tooltip,
} from '@material-ui/core';
import { Delete as DeleteIcon, Add as AddIcon, Info as InfoIcon } from '@material-ui/icons';
import AsyncSelect from 'react-select/async';
import ApiService from '../_services/ApiService';
import TimeCraftInputService from '../_services/TimeCraftInputService';

export interface InstructorAssignment {
  instructorId: number;
  code: string;
  name: string;
  role: 'primary' | 'secondary';
  remarks?: string | null;
}

interface InstructorOption {
  value: number;
  label: string;
  code: string;
  email: string;
}

interface SuggestedInstructor {
  instructorId: number;
  name: string;
  code: string;
  email: string;
  score: number;
  tags: string[];
  avgHoursPerWeek: number;
  maxHoursPerWeek: number;
  hourlyRate: number | null;
}

interface InstructorAssignmentDialogProps {
  open: boolean;
  cypId?: number;
  moduleCode?: string;
  moduleName?: string;
  currentInstructors: InstructorAssignment[];
  onSave: (instructors: InstructorAssignment[]) => Promise<void>;
  onClose: () => void;
}

const TAG_COLORS: Record<string, string> = {
  'Taught before': '#2563eb',
  'Specialist': '#059669',
  'Subject expert': '#10b981',
  'Can teach': '#6b7280',
  'Available': '#0d9488',
  'Has capacity': '#7c3aed',
};

const SCORE_TOOLTIP = [
  'Score (0–100) is computed from:',
  '',
  '  +30  Taught this module before',
  '  +20  Specialist (mapped to ≤10 courses)',
  '  +15  Subject expert (mapped to ≤30 courses)',
  '  +10  Can teach (mapped to ≤100 courses)',
  '  +15  Available (≤2 unavailable days/week)',
  '  +15  Has capacity (avg load < 80% of max)',
  '  +20  Base (active instructor)',
].join('\n');

const getScoreColor = (score: number) => {
  if (score >= 70) return '#059669';
  if (score >= 40) return '#d97706';
  return '#dc2626';
};

const InstructorAssignmentDialog: React.FC<InstructorAssignmentDialogProps> = ({
  open,
  cypId,
  moduleCode,
  moduleName,
  currentInstructors,
  onSave,
  onClose,
}) => {
  const [instructors, setInstructors] = useState<InstructorAssignment[]>(currentInstructors);
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState<SuggestedInstructor[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // Reset state when dialog opens with new data
  React.useEffect(() => {
    if (open) {
      setInstructors([...currentInstructors]);
      if (cypId) {
        setLoadingSuggestions(true);
        TimeCraftInputService.getSuggestedInstructors(cypId)
          .then((response: any) => {
            setSuggestions(response.data?.data || []);
          })
          .catch(() => setSuggestions([]))
          .finally(() => setLoadingSuggestions(false));
      }
    } else {
      setSuggestions([]);
    }
  }, [open, currentInstructors, cypId]);

  const loadInstructorOptions = async (inputValue: string): Promise<InstructorOption[]> => {
    if (!inputValue || inputValue.length < 2) return [];
    try {
      const response: any = await ApiService.searchInstructors(inputValue);
      const data = response.data?.data || response.data || [];
      return data.map((inst: any) => ({
        value: inst.id,
        label: `${inst.name}${inst.code ? ` (${inst.code})` : ''}`,
        code: inst.code || '',
        email: inst.email || '',
      }));
    } catch (error) {
      console.error('Failed to search instructors:', error);
      return [];
    }
  };

  const handleAddInstructor = (selected: InstructorOption | null) => {
    if (!selected) return;

    // Check if already added
    if (instructors.some(i => i.instructorId === selected.value)) return;

    // First instructor is primary, rest are secondary
    const role: 'primary' | 'secondary' = instructors.length === 0 ? 'primary' : 'secondary';

    setInstructors([
      ...instructors,
      {
        instructorId: selected.value,
        code: selected.code,
        name: selected.label.replace(` (${selected.code})`, ''), // Clean name
        role,
        remarks: null,
      },
    ]);
  };

  const handleAddSuggested = (suggested: SuggestedInstructor) => {
    if (instructors.some(i => i.instructorId === suggested.instructorId)) return;
    const role: 'primary' | 'secondary' = instructors.length === 0 ? 'primary' : 'secondary';
    setInstructors([
      ...instructors,
      {
        instructorId: suggested.instructorId,
        code: suggested.code,
        name: suggested.name,
        role,
        remarks: null,
      },
    ]);
  };

  const handleRemoveInstructor = (instructorId: number) => {
    const updated = instructors.filter(i => i.instructorId !== instructorId);
    // If we removed the primary and there are others, promote the first to primary
    if (updated.length > 0 && !updated.some(i => i.role === 'primary')) {
      updated[0].role = 'primary';
    }
    setInstructors(updated);
  };

  const handleToggleRole = (instructorId: number) => {
    setInstructors(
      instructors.map(i => {
        if (i.instructorId === instructorId) {
          return { ...i, role: i.role === 'primary' ? 'secondary' : 'primary' };
        }
        return i;
      })
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(instructors);
      onClose();
    } catch (error: any) {
      alert('Failed to save instructors: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box>
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            Assign Instructors
          </Typography>
          {moduleCode && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
              {moduleCode}{moduleName ? ` - ${moduleName}` : ''}
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Instructor search */}
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 13, color: 'var(--text-body)' }}>
              Search & Add Instructor
            </label>
            <AsyncSelect
              loadOptions={loadInstructorOptions}
              onChange={(selected) => handleAddInstructor(selected as InstructorOption | null)}
              value={null}
              placeholder="Type instructor name or code (min 2 chars)..."
              isClearable
              noOptionsMessage={({ inputValue }) =>
                inputValue.length < 2 ? 'Type at least 2 characters' : 'No instructors found'
              }
              styles={{
                control: (base: any) => ({ ...base, minHeight: 38, fontSize: 14 }),
                menu: (base: any) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </div>

          {/* Suggested Instructors */}
          {cypId && (
            <Box>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
                <Typography variant="subtitle2" style={{ color: 'var(--text-body)', fontSize: 13 }}>
                  Suggested Instructors
                </Typography>
                <Tooltip
                  title={<span style={{ whiteSpace: 'pre-line', fontSize: 12 }}>{SCORE_TOOLTIP}</span>}
                  placement="right"
                  arrow
                >
                  <InfoIcon style={{ fontSize: 16, color: 'var(--text-faint)', cursor: 'help' }} />
                </Tooltip>
              </Box>
              {loadingSuggestions ? (
                <Box style={{ textAlign: 'center', padding: 12 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : suggestions.length > 0 ? (
                <Box style={{
                  border: '1px solid var(--brand-primary-subtle)',
                  borderRadius: 6,
                  backgroundColor: 'var(--surface-page)',
                  maxHeight: 240,
                  overflowY: 'auto',
                }}>
                  {suggestions.map((s) => {
                    const alreadyAdded = instructors.some(i => i.instructorId === s.instructorId);
                    return (
                      <Box
                        key={s.instructorId}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px 10px',
                          borderBottom: '1px solid var(--brand-primary-subtle)',
                          gap: 8,
                        }}
                      >
                        {/* Score badge */}
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          backgroundColor: getScoreColor(s.score),
                          color: 'var(--text-on-brand)',
                          fontSize: 12,
                          fontWeight: 700,
                          flexShrink: 0,
                        }}>
                          {s.score}
                        </span>

                        {/* Name, tags, and workload info */}
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Box style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                            <Typography variant="body2" style={{ fontSize: 13, fontWeight: 500 }}>
                              {s.name}
                            </Typography>
                            {s.code && (
                              <span style={{ color: 'var(--text-faint)', fontSize: 11 }}>({s.code})</span>
                            )}
                          </Box>
                          <Box style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2, flexWrap: 'wrap' }}>
                            {s.tags.map((tag) => (
                              <span
                                key={tag}
                                style={{
                                  display: 'inline-block',
                                  padding: '1px 6px',
                                  borderRadius: 10,
                                  fontSize: 10,
                                  fontWeight: 600,
                                  backgroundColor: TAG_COLORS[tag] || '#6b7280',
                                  color: 'var(--text-on-brand)',
                                }}
                              >
                                {tag}
                              </span>
                            ))}
                            <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                              {s.avgHoursPerWeek}/{s.maxHoursPerWeek} hrs/wk
                            </span>
                            {s.hourlyRate != null && (
                              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                                ${s.hourlyRate}/hr
                              </span>
                            )}
                          </Box>
                        </Box>

                        {/* Add button or "Added" text */}
                        {alreadyAdded ? (
                          <Typography variant="caption" style={{ color: 'var(--text-faint)', fontStyle: 'italic', fontSize: 11, flexShrink: 0 }}>
                            Added
                          </Typography>
                        ) : (
                          <IconButton size="small" onClick={() => handleAddSuggested(s)} title="Add instructor">
                            <AddIcon fontSize="small" style={{ color: 'var(--brand-primary)' }} />
                          </IconButton>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
                  No suggestions available for this module.
                </Typography>
              )}
            </Box>
          )}

          {/* Current instructors list */}
          {instructors.length > 0 ? (
            <Box>
              <Typography variant="subtitle2" style={{ marginBottom: 8, color: 'var(--text-body)' }}>
                Assigned Instructors ({instructors.length})
              </Typography>
              {instructors.map((inst, idx) => (
                <Box
                  key={inst.instructorId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    backgroundColor: idx % 2 === 0 ? '#f9f9f9' : '#fff',
                    borderRadius: 4,
                    marginBottom: 4,
                    border: '1px solid var(--border-subtle)',
                  }}
                >
                  <Box style={{ flex: 1 }}>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      {inst.name}
                    </Typography>
                    {inst.code && (
                      <Typography variant="caption" color="textSecondary">
                        {inst.code}
                      </Typography>
                    )}
                  </Box>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span
                      onClick={() => handleToggleRole(inst.instructorId)}
                      style={{
                        cursor: 'pointer',
                        padding: '2px 10px',
                        borderRadius: 12,
                        fontSize: 11,
                        fontWeight: 600,
                        backgroundColor: inst.role === 'primary' ? '#2563eb' : '#6b7280',
                        color: 'white',
                      }}
                    >
                      {inst.role === 'primary' ? 'Primary' : 'Secondary'}
                    </span>
                    <IconButton size="small" onClick={() => handleRemoveInstructor(inst.instructorId)}>
                      <DeleteIcon fontSize="small" style={{ color: '#d32f2f' }} />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box
              style={{
                padding: 24,
                textAlign: 'center',
                backgroundColor: 'var(--surface-sunken)',
                borderRadius: 4,
              }}
            >
              <Typography variant="body2" color="textSecondary">
                No instructors assigned. Use the search above to add instructors.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions style={{ padding: '16px 24px' }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={saving}
          style={{ minWidth: 120 }}
        >
          {saving ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InstructorAssignmentDialog;
